/* eslint-disable max-lines-per-function */
import { anstellungstyp } from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/anstellungstyp';
import { ErinnerungsZeiten } from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/erinnerungsZeiten';
import { FristenGrund } from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/fristenGrund';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import z from 'zod';

export function useAnstellungsverhaeltnisSchema() {
  const { t } = useTranslation();
  const anstellungSchema = z
    .object({
      anstellungstyp: z
        .enum(anstellungstyp)
        .nullable()
        .refine((val) => !!val, {
          error: t(
            'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.anstellung.form.anstellungstyp.validationError',
          ),
        }),
      eintrittsdatum: z.iso
        .datetime()
        .nullable()
        .refine(
          (value) => {
            return !!value;
          },
          {
            error: t(
              'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.anstellung.form.eintrittsdatum.validationError',
            ),
          },
        ),
      befristet: z.boolean(),
      befristung: z.iso.datetime().nullable(),
    })
    .superRefine((data, ctx) => {
      if (data.befristet) {
        if (!data.befristung) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.anstellung.form.befristetBis.validationError',
            ),
            path: ['befristung'],
          });
        } else if (
          data.eintrittsdatum &&
          dayjs(data.befristung).isSameOrBefore(dayjs(data.eintrittsdatum))
        ) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.anstellung.form.befristetBis.validationErrorBeforeEintrittsdatum',
            ),
            path: ['befristung'],
          });
        }
      }
    });

  const FristenSchema = z
    .object({
      fristdatum: z.iso
        .datetime()
        .nullable()
        .refine(
          (value) => {
            return !!value;
          },
          {
            error: t(
              'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.fristdatum.validationError',
            ),
          },
        ),
      grund: z
        .string()
        .nullable()
        .refine((value) => !!value, {
          error: t(
            'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.grund.validationError',
          ),
        }),
      andererGrund: z.string().nullable(),
      bemerkung: z.string().max(250).nullable(),
      erinnerung: z.boolean(),
      erinnerungsZeit: z
        .string()
        .array()
        .refine((value) => {
          return value.every((v) => Object.keys(ErinnerungsZeiten).includes(v));
        }),
      erinnerungAn: z.string().array(),
    })
    .superRefine((data, ctx) => {
      if (data.grund === FristenGrund.ANDERER) {
        if (!data.andererGrund?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.andererGrund.validationError',
            ),
            path: ['andererGrund'],
          });
        } else if (data.andererGrund.trim().length > 50) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.andererGrund.validationErrorMaxLength',
            ),
            path: ['andererGrund'],
          });
        }
      }
      if (data.erinnerung) {
        if (data.erinnerungsZeit.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.fristen.erinnerungsZeit.validationError',
            ),
            path: ['erinnerungsZeit'],
          });
        }
        if (data.erinnerungAn.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.erinnerungAn.validationError',
            ),
            path: ['erinnerungAn'],
          });
        }
      }
    });

  return {
    FristenSchema,
    anstellungSchema,
  };
}
